import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WordListTitle from '../components/WordListTitle';
import WordCard from '../components/WordCard';
import WordNavigation from '../components/WordNavigation';
import FullWordList from '../components/FullWordList';
import DonationBanner from '../components/DonationBanner';
import api from '../services/api';
import './VocabularyShared.css';

const TOEFLVocabularyPage = () => {
  const [wordList, setWordList] = useState([]);
  const [currentWord, setCurrentWord] = useState(null);
  const [currentWordData, setCurrentWordData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const titleData = {
    title: "Word collections",
    heading: "TOEFL Vocabulary",
    description: "Master essential vocabulary for the TOEFL exam. These carefully selected words frequently appear in reading passages, listening sections, and writing tasks. Understanding these words will help you comprehend complex academic texts and express yourself more effectively in English."
  };

  // Fetch the list of TOEFL words
  useEffect(() => {
    const fetchWordList = async () => {
      try {
        setLoading(true);
        const words = await api.getTOEFLWords();
        // Ensure unique words in the list
        const uniqueWords = [...new Set(words.map(word => typeof word === 'object' ? word.word : word))];
        setWordList(uniqueWords);
        if (uniqueWords.length > 0) {
          setCurrentWord(uniqueWords[0]);
        }
      } catch (err) {
        setError('Failed to load TOEFL words');
      } finally {
        setLoading(false);
      }
    };

    fetchWordList();
  }, []);

  // Fetch detailed data for the current word
  useEffect(() => {
    const fetchCurrentWordData = async () => {
      if (!currentWord) return;
      
      try {
        setLoading(true);
        // Fetch all data in parallel
        const [wordData, examples, similarWords, mnemonicsData] = await Promise.all([
          api.getTOEFLWordData(currentWord),
          api.getUsageExamples(currentWord),
          api.getSimilarWords(currentWord),
          api.getWordMnemonics(currentWord)
        ]);
        
        setCurrentWordData({
          ...wordData,
          examples: examples || {},
          similarWords: similarWords || [],
          mnemonics: mnemonicsData.mnemonics || []
        });
      } catch (err) {
        setError(`Failed to load data for ${currentWord}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentWordData();
  }, [currentWord]);

  const handleWordSelect = (word) => {
    setCurrentWord(word);
  };

  if (loading && !wordList.length) {
    return (
      <div className="sat-vocabulary-page">
        <Header />
        <main className="main-content">
          <section className="title-section">
            <WordListTitle {...titleData} />
          </section>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-message">Loading TOEFL vocabulary...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sat-vocabulary-page">
        <Header />
        <main className="main-content">
          <section className="title-section">
            <WordListTitle {...titleData} />
          </section>
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="sat-vocabulary-page">
      <Header />
      <main className="main-content">
        <section className="title-section">
          <WordListTitle {...titleData} />
        </section>
        
        <div className="sticky-container">
          <section className="word-card-section">
            {currentWordData ? (
              <>
                <WordCard 
                  word={currentWordData.word}
                  pronunciation={currentWordData.ipa}
                  pronunciationAudio={currentWordData.pronunciationAudio}
                  partOfSpeech={currentWordData.partsOfSpeech}
                  definition={currentWordData.quickDefinition}
                  example={currentWordData.quickExample}
                  personaExplanation={currentWordData.explanations}
                  examples={currentWordData.examples || {}}
                  similarWords={currentWordData.similarWords || []}
                  meanings={currentWordData.meanings || []}
                  mnemonics={currentWordData.mnemonics || []}
                  wordListTags={currentWordData.wordListTags}
                />
              </>
            ) : (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-message">Loading word details...</p>
              </div>
            )}
          </section>

          <WordNavigation 
            onPrevious={() => {
              const currentIndex = wordList.indexOf(currentWord);
              if (currentIndex > 0) {
                setCurrentWord(wordList[currentIndex - 1]);
              }
            }}
            onNext={() => {
              const currentIndex = wordList.indexOf(currentWord);
              if (currentIndex < wordList.length - 1) {
                setCurrentWord(wordList[currentIndex + 1]);
              }
            }}
            onShuffle={() => {
              const randomIndex = Math.floor(Math.random() * wordList.length);
              setCurrentWord(wordList[randomIndex]);
            }}
            hasPrevious={wordList.indexOf(currentWord) > 0}
            hasNext={wordList.indexOf(currentWord) < wordList.length - 1}
          />
        </div>
        
        <section className="full-word-list-section">
          <FullWordList 
            words={wordList} 
            onWordSelect={handleWordSelect} 
          />
        </section>

        <section className="donation-section">
          <DonationBanner />
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TOEFLVocabularyPage; 